'use client';
import React from 'react';

import type { SignUpFormData } from './SellerSignUpForm';
import Icon from '@/components/ui/AppIcon';

interface Props {
  form: ReturnType<typeof import('react-hook-form').useForm<SignUpFormData>>;
}

const CATEGORIES = [
  { value: '', label: 'Select a category' },
  { value: 'electronics', label: 'Electronics & Gadgets' },
  { value: 'fashion', label: 'Fashion & Apparel' },
  { value: 'food-grocery', label: 'Food & Grocery' },
  { value: 'home-kitchen', label: 'Home & Kitchen' },
  { value: 'health-beauty', label: 'Health & Beauty' },
  { value: 'sports', label: 'Sports & Fitness' },
  { value: 'books-stationery', label: 'Books & Stationery' },
  { value: 'eco-products', label: 'Eco Products & Sustainability' },
  { value: 'other', label: 'Other' },
];

const SELLER_TYPES = [
  {
    value: 'hyperlocal',
    label: 'Hyperlocal',
    icon: '🌿',
    description: 'Serve customers in your city with same-day delivery',
  },
  {
    value: 'national',
    label: 'National',
    icon: '🌍',
    description: 'Reach customers across all of India',
  },
  {
    value: 'both',
    label: 'Both',
    icon: '⚡',
    description: 'Maximum reach — hyperlocal + national',
  },
];

export default function Step2Store({ form }: Props) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const selectedType = watch('sellerType');

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-700 text-foreground mb-1">Store Details</h2>
        <p className="text-sm text-muted-foreground">
          Tell customers about your store and how you want to sell.
        </p>
      </div>

      {/* Store Name */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1.5">
          Store Name <span className="text-error">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-2">
          This is the name customers will see on the Bozaat marketplace
        </p>
        <input
          type="text"
          placeholder="e.g. Green Harvest Store"
          className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
          {...register('storeName', {
            required: 'Store name is required',
            minLength: { value: 3, message: 'Store name must be at least 3 characters' },
          })}
        />
        {errors.storeName && (
          <p className="text-xs text-error mt-1.5 flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={12} />
            {errors.storeName.message}
          </p>
        )}
      </div>

      {/* Business Category */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1.5">
          Business Category <span className="text-error">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-2">
          Primary product category — affects your store&apos;s eco score algorithm
        </p>
        <select
          className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus"
          {...register('businessCategory', {
            required: 'Please select a business category',
          })}
        >
          {CATEGORIES.map((cat) => (
            <option key={`cat-${cat.value}`} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
        {errors.businessCategory && (
          <p className="text-xs text-error mt-1.5 flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={12} />
            {errors.businessCategory.message}
          </p>
        )}
      </div>

      {/* Store Description */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1.5">
          Store Description <span className="text-error">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-2">
          Tell customers what makes your store eco-friendly and unique (min. 30 characters)
        </p>
        <textarea
          rows={4}
          placeholder="Describe your store, your eco-friendly products, and what makes your business sustainable…"
          className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground resize-none"
          {...register('storeDescription', {
            required: 'Store description is required',
            minLength: {
              value: 30,
              message: 'Description must be at least 30 characters',
            },
          })}
        />
        {errors.storeDescription && (
          <p className="text-xs text-error mt-1.5 flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={12} />
            {errors.storeDescription.message}
          </p>
        )}
      </div>

      {/* Seller Type */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1.5">
          How do you want to sell? <span className="text-error">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-3">
          You can change this later from your store settings
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {SELLER_TYPES.map((type) => (
            <button
              key={`seller-type-${type.value}`}
              type="button"
              onClick={() =>
                setValue('sellerType', type.value as 'hyperlocal' | 'national' | 'both', {
                  shouldValidate: true,
                })
              }
              className={`relative border-2 rounded-xl p-4 text-left transition-all duration-150 ${
                selectedType === type.value
                  ? 'border-primary bg-green-50' :'border-border bg-card hover:border-primary/40 hover:bg-muted/40'
              }`}
            >
              {selectedType === type.value && (
                <div className="absolute top-2.5 right-2.5 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="CheckIcon" size={10} className="text-white" />
                </div>
              )}
              <div className="text-2xl mb-2">{type.icon}</div>
              <div className="text-sm font-700 text-foreground mb-1">
                {type.label}
              </div>
              <div className="text-xs text-muted-foreground leading-snug">
                {type.description}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}